import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useCurrentUser, useMyRewards, useMyBadges } from '../hooks/useHunt'
import { Button } from '../components/common/Button'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { BottomNav } from '../components/common/BottomNav'

export default function Profile() {
  const { user, signOut } = useAuth()
  const { data: profile, isLoading: profileLoading } = useCurrentUser()
  const { data: rewardsData, isLoading: rewardsLoading } = useMyRewards()
  const { data: badgesData, isLoading: badgesLoading } = useMyBadges()

  const handleSignOut = async () => {
    await signOut()
  }

  if (profileLoading) {
    return <LoadingSpinner className="min-h-screen" />
  }

  return (
    <div className="min-h-screen bg-cream-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-to-br from-primary-500 to-primary-700 text-white">
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/80 hover:text-white transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div className="flex-1">
              <h1 className="text-xl font-bold">{user?.display_name || 'Profile'}</h1>
              <p className="text-primary-100 text-sm">{user?.email}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-xl font-bold border-2 border-white/30">
              {user?.display_name?.[0]?.toUpperCase() || 'U'}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        {profile?.stats && (
          <div className="grid grid-cols-2 gap-4">
            <Card padding="md" className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-3xl font-bold text-primary-600">{profile.stats.hunts_completed}</p>
              </div>
              <p className="text-sm text-brown-500">Hunts Completed</p>
            </Card>
            <Card padding="md" className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <p className="text-3xl font-bold text-primary-600">{profile.stats.badges_earned}</p>
              </div>
              <p className="text-sm text-brown-500">Badges Earned</p>
            </Card>
            <Card padding="md" className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-3xl font-bold text-primary-600">{profile.stats.total_checkins}</p>
              </div>
              <p className="text-sm text-brown-500">Total Check-ins</p>
            </Card>
            <Card padding="md" className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <p className="text-3xl font-bold text-primary-600">
                  {profile.stats.total_distance_meters > 1000
                    ? `${(profile.stats.total_distance_meters / 1000).toFixed(1)}km`
                    : `${Math.round(profile.stats.total_distance_meters)}m`}
                </p>
              </div>
              <p className="text-sm text-brown-500">Distance Traveled</p>
            </Card>
          </div>
        )}

        {/* Badges */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-brown-900">My Badges</h2>
            <Link to="/badges" className="text-primary-600 text-sm font-medium hover:text-primary-700">
              View All
            </Link>
          </div>
          {badgesLoading ? (
            <LoadingSpinner />
          ) : badgesData?.badges.length === 0 ? (
            <Card variant="parchment" padding="md">
              <p className="text-brown-600 text-center">
                No badges yet. Complete hunts to earn badges!
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {badgesData?.badges.map((badge) => (
                <Card key={badge.id} padding="sm" className="text-center">
                  <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center overflow-hidden">
                    {badge.icon_url ? (
                      <img src={badge.icon_url} alt={badge.name} className="w-12 h-12" />
                    ) : (
                      <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    )}
                  </div>
                  <p className="font-medium text-brown-900 text-sm truncate">{badge.name}</p>
                  <p className={`text-xs capitalize ${
                    badge.rarity === 'legendary' ? 'text-primary-600 font-semibold' :
                    badge.rarity === 'epic' ? 'text-purple-600' :
                    badge.rarity === 'rare' ? 'text-blue-600' :
                    badge.rarity === 'uncommon' ? 'text-success-600' :
                    'text-brown-500'
                  }`}>
                    {badge.rarity}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Hunt History */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-brown-900">Hunt History</h2>
            <Link to="/history" className="text-primary-600 text-sm font-medium hover:text-primary-700">
              View All
            </Link>
          </div>
          <Link to="/history" className="block">
            <Card padding="md" className="hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-brown-900">All hunts you've played</p>
                  <p className="text-sm text-brown-500">
                    {(profile?.stats?.hunts_completed ?? 0) + (profile?.stats?.hunts_in_progress ?? 0)} total
                  </p>
                </div>
                <svg className="w-5 h-5 text-brown-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Card>
          </Link>
        </section>

        {/* My Rewards */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-brown-900">My Rewards</h2>
            <Link to="/rewards" className="text-primary-600 text-sm font-medium hover:text-primary-700">
              View All
            </Link>
          </div>
          <Link to="/rewards" className="block">
            <Card padding="md" className="hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-brown-900">Promo codes & rewards</p>
                  <p className="text-sm text-brown-500">Earned from completed hunts</p>
                </div>
                <svg className="w-5 h-5 text-brown-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Card>
          </Link>
        </section>

        {/* Active hunts */}
        {profile?.stats?.hunts_in_progress ? (
          <section>
            <h2 className="text-lg font-semibold text-brown-900 mb-3">Active Hunts</h2>
            <Card variant="parchment" padding="md">
              <div className="flex items-center gap-2 mb-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-primary-500"></span>
                </span>
                <p className="text-brown-700">
                  You have {profile.stats.hunts_in_progress} hunt{profile.stats.hunts_in_progress > 1 ? 's' : ''} in progress.
                </p>
              </div>
              <Link to="/hunts" className="text-primary-600 font-medium text-sm hover:text-primary-700">
                View hunts →
              </Link>
            </Card>
          </section>
        ) : null}

        {/* Rewards */}
        <section>
          <h2 className="text-lg font-semibold text-brown-900 mb-3">My Rewards</h2>
          {rewardsLoading ? (
            <LoadingSpinner />
          ) : rewardsData?.rewards.length === 0 ? (
            <Card variant="parchment" padding="md">
              <p className="text-brown-600 text-center">
                No rewards yet. Complete hunts to earn rewards!
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {rewardsData?.rewards.slice(0, 5).map((reward) => (
                <Card key={reward.id} padding="md">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      {reward.image_url ? (
                        <img src={reward.image_url} alt={reward.name} className="w-8 h-8" />
                      ) : (
                        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-brown-900">{reward.name}</p>
                      <p className="text-sm text-brown-500">{reward.hunt_title}</p>
                    </div>
                    <span className="text-xs text-brown-400 capitalize">{reward.type}</span>
                  </div>
                  {reward.code && (
                    <div className="mt-2 p-2 bg-cream-100 rounded text-center font-mono text-sm text-brown-800">
                      {reward.code}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Sign out */}
        <Button variant="secondary" className="w-full" onClick={handleSignOut}>
          Sign Out
        </Button>
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  )
}
